import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import type { NavLink as NavLinkType } from '@gills/shared';
import { useAuth } from '../lib/auth';
import { useCart } from '../lib/cart';
import { useStore } from '../lib/store';
import { Button } from './ui';

function DesktopNavItem({ link }: { link: NavLinkType }) {
  const [open, setOpen] = useState(false);
  const timer = useRef<number | null>(null);

  // A small close delay stops the menu vanishing when the pointer crosses the
  // gap between the trigger and the panel.
  const scheduleClose = () => {
    timer.current = window.setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => {
    if (timer.current) window.clearTimeout(timer.current);
  };

  useEffect(() => () => cancelClose(), []);

  if (link.children.length === 0) {
    return (
      <NavLink
        to={link.href}
        className={({ isActive }) =>
          `px-3 py-2 text-xs uppercase tracking-widest transition-opacity hover:opacity-60 ${
            isActive ? 'text-ink underline underline-offset-8' : 'text-ink'
          }`
        }
      >
        {link.label}
      </NavLink>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
    >
      <Link
        to={link.href}
        className="flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-widest text-ink transition-opacity hover:opacity-60"
        aria-expanded={open}
        aria-haspopup="true"
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <span aria-hidden="true" className="text-[8px]">
          ▼
        </span>
      </Link>

      {open && (
        <div
          className="absolute left-0 top-full z-40 min-w-56 border border-border bg-background py-2 shadow-lg"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {link.children.map((child) => (
            <Link
              key={`${child.href}-${child.label}`}
              to={child.href}
              className="block px-4 py-2 text-xs text-ink transition-colors hover:bg-surface"
              onClick={() => setOpen(false)}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileMenu({ links, onClose }: { links: NavLinkType[]; onClose: () => void }) {
  return (
    <div className="border-t border-border bg-background lg:hidden">
      <nav className="page-container flex flex-col py-4">
        {links.map((link) => (
          <div key={`${link.href}-${link.label}`} className="border-b border-border/60 py-1">
            <Link
              to={link.href}
              onClick={onClose}
              className="block py-2 text-sm uppercase tracking-widest"
            >
              {link.label}
            </Link>
            {link.children.length > 0 && (
              <div className="flex flex-col pb-2 pl-4">
                {link.children.map((child) => (
                  <Link
                    key={`${child.href}-${child.label}`}
                    to={child.href}
                    onClick={onClose}
                    className="py-1.5 text-xs text-muted"
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>
    </div>
  );
}

export function Header() {
  const { data } = useStore();
  const { itemCount, open } = useCart();
  const { principal } = useAuth();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);

  const links = data?.nav?.header ?? [];
  const announcement = data?.store?.announcementEnabled ? data.store.announcement : '';

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background">
      {announcement && (
        <div className="bg-primary py-2 text-center text-[11px] uppercase tracking-widest text-primary-ink">
          {announcement}
        </div>
      )}

      <div className="page-container flex items-center justify-between gap-4 py-4">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="cursor-pointer p-2 text-lg lg:hidden"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? '✕' : '☰'}
        </button>

        <Link to="/" className="shrink-0">
          <span className="font-heading text-xl tracking-wide md:text-2xl">
            {data?.store?.name ?? "Gill's Handmade"}
          </span>
        </Link>

        <nav className="hidden flex-1 items-center justify-center lg:flex">
          {links.map((link) => (
            <DesktopNavItem key={`${link.href}-${link.label}`} link={link} />
          ))}
        </nav>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSearchOpen((v) => !v)}
            className="cursor-pointer p-2 text-sm"
            aria-label="Search"
            aria-expanded={searchOpen}
          >
            ⌕
          </button>

          <Link
            to="/account"
            className="hidden p-2 text-xs uppercase tracking-widest sm:block"
          >
            {principal?.isAdmin ? 'Admin' : 'Orders'}
          </Link>

          <button
            type="button"
            onClick={open}
            className="relative cursor-pointer p-2 text-xs uppercase tracking-widest"
            aria-label={`Basket, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
          >
            Basket
            {itemCount > 0 && (
              <span className="ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] text-primary-ink">
                {itemCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-border bg-background">
          <form
            className="page-container flex items-center gap-3 py-4"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = query.trim();
              if (!trimmed) return;
              navigate(`/search?q=${encodeURIComponent(trimmed)}`);
              setSearchOpen(false);
              setQuery('');
            }}
          >
            <input
              ref={searchInput}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search for earrings, necklaces, colours…"
              className="flex-1 border-b border-border bg-transparent py-2 text-sm outline-none placeholder:text-muted focus:border-ink"
              aria-label="Search products"
            />
            <Button type="submit" size="sm">
              Search
            </Button>
          </form>
        </div>
      )}

      {menuOpen && <MobileMenu links={links} onClose={() => setMenuOpen(false)} />}
    </header>
  );
}
